// SalesQuotationPage.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { Edit, Check, Close, Print, Add } from "@mui/icons-material";
import { getFetchSalesQuotationList } from "../../services/api";
import { useNavigate } from "react-router-dom";
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
} from "../../data/content";
import Navbar from "../../layout/Navbar";
import Sidebar from "../../layout/Sidebar";
import { useHasVerticalScroll } from "../../data/functions";

export default function SalesQuotationPage() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const hasScroll = useHasVerticalScroll();

  const handleSidebarToggle = () => setSidebarOpen((open) => !open);
  const handleSidebarClose = () => setSidebarOpen(false);
  const baseWidth = hasScroll ? "99vw" : "100vw";
  const sidebarWidth = sidebarOpen
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH_COLLAPSED;
  const navigate = useNavigate();

  const [itemsList, setItemsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [selected, setSelected] = useState([]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const fetchItemsData = async () => {
    try {
      setLoading(true);

      const response = await getFetchSalesQuotationList({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      });
      // console.log(response.data);

      const itemsData = response.data.data || [];

      setItemsList([...itemsData]);
      setRowCount(response.data.pagenation?.total || 0);
    } catch (err) {
      console.error("Failed to fetch quotation List", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItemsData();
  }, [paginationModel]);

  const handleToggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) setSelected(itemsList.map((r) => r.quote_id));
    else setSelected([]);
  };

  const handleExport = () => {
    // placeholder: hook this to your export logic
    // console.log("Exporting selected:", selected);
    alert(`Exporting ${selected.length} selected rows (see console)`);
  };

  const onHandleAdd = () => {
    navigate("/sales-quotation-create");
  };

  const onHandleEdit = (row) => {
    navigate(`/sales-quotation-update/${row.quote_id}`);
  };

  return (
    <Box
      sx={{
        width: baseWidth,
        height: "auo",
        backgroundColor: "#f5f7fb",
        display: "flex",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
      <Box>
        <Navbar onMenuClick={handleSidebarToggle} sidebarOpen={sidebarOpen} />

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            width: `calc(${baseWidth} - ${sidebarWidth}px)`,
            minHeight: "calc( 100vh - 56px)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              📄 Sales Quotation List
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<Print />}
                onClick={handleExport}
                disabled={selected.length === 0}
              >
                Export Selected
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={onHandleAdd}
              >
                New
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ borderRadius: "10px" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell>
                      <Checkbox
                        onChange={handleSelectAll}
                        checked={
                          selected.length === itemsList.length &&
                          itemsList.length > 0
                        }
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < itemsList.length
                        }
                      />
                    </TableCell>
                    <TableCell>Doc Entry</TableCell>
                    <TableCell>Doc Num</TableCell>
                    <TableCell>Doc Date</TableCell>
                    <TableCell>Customer Code</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>Document Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" rowSpan={2}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : itemsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemsList.map((row, index) => (
                      <TableRow
                        key={row.quote_id}
                        sx={{
                          backgroundColor:
                            index % 2 === 1 ? "#fafafa" : "inherit",
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected(row.quote_id)}
                            onChange={() => handleToggle(row.quote_id)}
                          />
                        </TableCell>
                        <TableCell>{row.quote_id}</TableCell>
                        <TableCell>{row.doc_number}</TableCell>
                        <TableCell>{row.doc_due_date}</TableCell>
                        <TableCell>{row.customer.subcompany}</TableCell>
                        <TableCell>{row.customer.name}</TableCell>
                        <TableCell>{row.doc_type}</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <IconButton
                            color="primary"
                            onClick={() => onHandleEdit(row)}
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {/* Pagination */}
              <TablePagination
                component="div"
                count={rowCount}
                page={paginationModel.page}
                onPageChange={(_, newPage) =>
                  setPaginationModel((prev) => ({ ...prev, page: newPage }))
                }
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={(e) =>
                  setPaginationModel({
                    page: 0,
                    pageSize: parseInt(e.target.value, 10),
                  })
                }
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}
